import { Button } from "@mui/material";
import { Box, Stack } from "@mui/system";
import { Formik, FormikErrors } from "formik";
import * as Yup from "yup";

import NoDataAlert from "@/common/components/AppAlerts/NoDataAlert";
import GeneralValidationError from "@/common/components/Error/GeneralValidationError";
import FormActions from "@/common/components/Form/FormActions";
import AppIcon from "@/common/components/Icons/AppIcon";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { useCurrentCurrency } from "@/common/hooks/useCurrentCurrency";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import {
  GeneralProductProfileDto,
  GeneralProductProfileInputDto,
  GeneralProductProfileOfEntityUpdateDto,
  GeneralProductProfileOfEntityUpdateResultDto,
} from "@/core/api/generated/v1";

import GeneralProductProfileInput from "./GeneralProductProfileInput";

export interface GeneralProductProfileOfEntityUpdateProps {
  initialValue: Nil<GeneralProductProfileDto>;
  saveFunc: (
    newValue: GeneralProductProfileOfEntityUpdateDto | undefined,
  ) => Promise<GeneralProductProfileOfEntityUpdateResultDto>;
  onSaved: (newValue: GeneralProductProfileOfEntityUpdateResultDto) => void;
}

export default function GeneralProductProfileOfEntityUpdate({
  initialValue,
  saveFunc,
  onSaved,
}: GeneralProductProfileOfEntityUpdateProps) {
  const mounted = useMounted();
  const { enqueueSnackbar } = useAppSnackbar();
  const currentCurrency = useCurrentCurrency();

  if (!currentCurrency) {
    return null;
  }

  return (
    <Formik<GeneralProductProfileOfEntityUpdateDto & BaseFormikValues>
      enableReinitialize={false}
      initialValues={{
        productProfile: initialValue
          ? { ...initialValue, currency: initialValue.currency || currentCurrency }
          : undefined,
        submit: "",
      }}
      validationSchema={Yup.object().shape({
        // name: Yup.string().required("Name is required"),
      })}
      onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
        try {
          const result = await saveFunc(values);
          enqueueSnackbar("Product profile saved.", { variant: "success" });
          onSaved(result);

          if (mounted.current) {
            setStatus({ success: true });
            setSubmitting(false);
          }
        } catch (err: any) {
          if (mounted.current) {
            ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
            setStatus({ success: false });
            setSubmitting(false);
          }
        }
      }}
    >
      {({ errors, handleSubmit, isSubmitting, values, setFieldValue }) => {
        return (
          <form noValidate onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <Box>
                {!values.productProfile && (
                  <Box>
                    <NoDataAlert fullWidth={false} />

                    <Stack
                      direction='row'
                      sx={{
                        height: "100%",
                        alignItems: "center",
                        justifyContent: { lg: "flex-end" },
                      }}
                    >
                      <Button
                        variant='text'
                        color='text'
                        size='small'
                        startIcon={<AppIcon of='add' />}
                        onClick={() => {
                          setFieldValue("productProfile", {
                            ...initialValue,
                            currency: initialValue?.currency || currentCurrency,
                          });
                        }}
                      >
                        Add profile
                      </Button>
                    </Stack>
                  </Box>
                )}

                {values.productProfile && (
                  <Stack spacing={1}>
                    <GeneralProductProfileInput
                      value={values.productProfile}
                      errors={errors?.productProfile as FormikErrors<GeneralProductProfileInputDto>}
                      onChange={(newValue) => {
                        setFieldValue("productProfile", newValue);
                      }}
                    />

                    <Stack
                      direction='row'
                      sx={{
                        height: "100%",
                        alignItems: "center",
                        justifyContent: { lg: "flex-end" },
                      }}
                    >
                      <Button
                        variant='text'
                        color='text'
                        size='small'
                        startIcon={<AppIcon of='remove' />}
                        onClick={() => {
                          setFieldValue("productProfile", undefined);
                        }}
                      >
                        Remove profile
                      </Button>
                    </Stack>
                  </Stack>
                )}
              </Box>

              <GeneralValidationError sx={{ my: 1 }} errors={errors} />

              <FormActions>
                <Button
                  color='primary'
                  loading={isSubmitting}
                  fullWidth
                  type='submit'
                  variant='contained'
                >
                  Save
                </Button>
              </FormActions>
            </Stack>
          </form>
        );
      }}
    </Formik>
  );
}
